/**
 * Survey API wrappers — admin-console 內的問卷題目 / 流程 / 匯入操作。
 *
 * 後端 routes(qubby_backend_new):
 *   GET    /api/survey/questions?avatarId=...
 *   GET    /api/survey/questions/:id?avatarId=...
 *   POST   /api/survey/questions             body { avatarId, ...payload }
 *   PUT    /api/survey/questions/:id         body { avatarId, ...payload }
 *   DELETE /api/survey/questions/:id?avatarId=...
 *   PUT    /api/survey/questions-reorder     body { avatarId, updates:[{questionId,newOrder}] }
 *   GET    /api/survey/templates?avatarId=...
 *   GET    /api/survey/flow/:avatarId
 *   PUT    /api/survey/flow/:avatarId        body QuestionFlowConfig
 *   POST   /api/survey/flow/:avatarId/validate  body { canvasFlow }
 *   POST   /api/survey/import                multipart { file }, avatarId in X-Avatar-Id
 *
 * 後端優先讀 `X-Avatar-Id` header 取 avatarId,fallback 到 query/body。
 */
import { api, get, post, put, del, download } from './client.js';

const enc = (s) => encodeURIComponent(s);
const hdr = (avatarId) => ({ 'X-Avatar-Id': avatarId });

// ── Questions ────────────────────────────────────────────────────

export function fetchQuestions(avatarId) {
    return get(`/api/survey/questions?avatarId=${enc(avatarId)}`, hdr(avatarId));
}

export function fetchQuestion(avatarId, questionId) {
    return get(`/api/survey/questions/${enc(questionId)}?avatarId=${enc(avatarId)}`, hdr(avatarId));
}

export function createQuestion(avatarId, payload) {
    return post(`/api/survey/questions`, { avatarId, ...payload }, hdr(avatarId));
}

export function updateQuestion(avatarId, questionId, payload) {
    return put(`/api/survey/questions/${enc(questionId)}`, { avatarId, ...payload }, hdr(avatarId));
}

export function deleteQuestion(avatarId, questionId) {
    return del(`/api/survey/questions/${enc(questionId)}?avatarId=${enc(avatarId)}`, hdr(avatarId));
}

/** updates: [{ questionId, newOrder }] */
export function reorderQuestions(avatarId, updates) {
    return put(`/api/survey/questions-reorder`, { avatarId, updates }, hdr(avatarId));
}

// ── Templates ────────────────────────────────────────────────────

export function fetchTemplates(avatarId) {
    return get(`/api/survey/templates?avatarId=${enc(avatarId)}`, hdr(avatarId));
}

// ── Flow ─────────────────────────────────────────────────────────

export function fetchFlow(avatarId) {
    return get(`/api/survey/flow/${enc(avatarId)}`, hdr(avatarId));
}

export function saveFlow(avatarId, config) {
    return put(`/api/survey/flow/${enc(avatarId)}`, config, hdr(avatarId));
}

export function validateFlow(avatarId, config) {
    return post(`/api/survey/flow/${enc(avatarId)}/validate`, config, hdr(avatarId));
}

// ── Import (multipart) ───────────────────────────────────────────

/**
 * 從 PDF/DOCX 匯入題目。
 * 回傳後端解析出的 question[] 草稿(尚未寫入,由前端確認後再批次 createQuestion)。
 */
export function importFromFile(avatarId, file) {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('avatarId', avatarId);
    return api('POST', '/api/survey/import', {
        body: fd,
        headers: hdr(avatarId),
    });
}

// ── Respondents（名單 + 數據聚合）─────────────────────────────────

/**
 * 受訪者列表（by phone 聚合）
 * params: { source, campaignId, completed, fromDate, toDate, search, page, pageSize, includeFake }
 */
export function fetchRespondents(avatarId, params = {}) {
    const qs = new URLSearchParams({ avatarId, ...Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ) });
    return get(`/api/survey/respondents?${qs.toString()}`, hdr(avatarId));
}

/** 該 phone (或 group key) 的所有 session timeline */
export function fetchRespondentByPhone(avatarId, { phone, key, hostId, includeFake } = {}) {
    const qs = new URLSearchParams({ avatarId });
    if (phone) qs.set('phone', phone);
    if (key) qs.set('key', key);
    if (hostId) qs.set('hostId', hostId);
    if (includeFake) qs.set('includeFake', '1');
    return get(`/api/survey/respondents/by-phone?${qs.toString()}`, hdr(avatarId));
}

/** 單一 session 完整詳情（含 callLog join） */
export function fetchSessionDetail(avatarId, sessionId, { hostId } = {}) {
    const qs = new URLSearchParams({ avatarId });
    if (hostId) qs.set('hostId', hostId);
    return get(`/api/survey/respondents/session/${enc(sessionId)}?${qs.toString()}`, hdr(avatarId));
}

/**
 * CSV 匯出（帶 Firebase token 的 fetch → blob 下載）。
 * ⚠️ 不能用 `<a href download>`：純導航不帶 Authorization → admin-console proxy 401。
 */
export function downloadRespondentsCsv(avatarId, params = {}) {
    const qs = new URLSearchParams({ avatarId, ...Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ) });
    return download(`/api/survey/respondents/export.csv?${qs.toString()}`, hdr(avatarId));
}

/** Campaign 維度統計（撥打/接通/答完率）*/
export function fetchCampaignStats(avatarId, hostId, campaignId) {
    const qs = new URLSearchParams({ avatarId, hostId, campaignId, includeFake: '1' });
    return get(`/api/survey/campaign-stats?${qs.toString()}`, hdr(avatarId));
}

/** 該 avatar 的所有 campaigns（filtered by avatarId）*/
export function fetchSurveyCampaigns(avatarId, hostId) {
    const qs = new URLSearchParams({ avatarId, hostId });
    return get(`/api/survey/campaigns?${qs.toString()}`, hdr(avatarId));
}

// ── Analytics（既有 endpoint，封裝 query）────────────────────────

export function fetchDistribution(avatarId, questionId, filters = {}) {
    const qs = new URLSearchParams({ avatarId, questionId, ...filters });
    return get(`/api/survey/analytics/distribution?${qs.toString()}`, hdr(avatarId));
}

export function fetchCrossAnalysis(avatarId, demographicField, questionId, filters = {}) {
    const qs = new URLSearchParams({ avatarId, demographicField, questionId, ...filters });
    return get(`/api/survey/analytics/cross-analysis?${qs.toString()}`, hdr(avatarId));
}

export function fetchDemographicFields(avatarId) {
    return get(`/api/survey/analytics/demographic-fields?avatarId=${enc(avatarId)}`, hdr(avatarId));
}

export function fetchDemographicOptions(avatarId, filters = {}) {
    const qs = new URLSearchParams({ avatarId, ...filters });
    return get(`/api/survey/analytics/demographic-options?${qs.toString()}`, hdr(avatarId));
}

export function fetchDemographicBreakdown(avatarId, demographicField, filters = {}) {
    const qs = new URLSearchParams({ avatarId, demographicField, ...filters });
    return get(`/api/survey/analytics/demographic-breakdown?${qs.toString()}`, hdr(avatarId));
}
